import { useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileUp,
  GripVertical,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SegmentedControl, SegmentedControlItem } from "@/components/ui/segmented-control";
import { cn } from "@/lib/utils";
import { fillUploadForm, MOCK_FILE_NAME } from "./fillUploadForm";
import { resetPrototype } from "./mock";
import {
  AFTER_SAVE_LABELS,
  FOCUS_SCENARIO,
  REIMPORT_LABELS,
  SCENARIOS,
  useAfterSave,
  useCatalogFixed,
  useImportScenario,
  useReimportPlacement,
  type AfterSaveVariant,
  type ImportScenario,
  type ReimportPlacement,
} from "./scenario";

/**
 * Prototype-only floating dev bar: pick how the next import goes wrong, then
 * run the wizard. The scenario is read when the file is submitted, so a run
 * already in flight keeps the one it started with.
 *
 * Reset sits at the top because it is the way back from whatever the dial
 * below it did. The bar collapses to a handle for looking at a screen it
 * covers, and it is draggable; position and collapsed state are remembered.
 *
 * Mounted only while the dev auth bypass is active (see App.tsx), so it cannot
 * reach a real build.
 */
const POS_KEY = "rie-dev-bar-pos";
const COLLAPSED_KEY = "rie-dev-bar-collapsed";
const OLD_STATES_KEY = "rie-dev-bar-old-states";

function loadPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through to default */
  }
  return { x: 12, y: 12 };
}

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota: the bar still works for this page view */
  }
}

export function PrototypeDevBar() {
  const { scenario, setScenario } = useImportScenario();
  const { afterSave, setAfterSave } = useAfterSave();
  const { reimportPlacement, setReimportPlacement } = useReimportPlacement();
  const { catalogFixed, setCatalogFixed } = useCatalogFixed();
  const [pos, setPos] = useState(loadPos);
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  // The states the prototype reproduced first; folded away, because the flow
  // above them is what this bar is for now.
  const [oldStatesOpen, setOldStatesOpen] = useState(() => {
    try {
      return localStorage.getItem(OLD_STATES_KEY) === "true";
    } catch {
      return false;
    }
  });
  const panel = useRef<HTMLDivElement>(null);
  const posRef = useRef(pos);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    // Kept inside the viewport: dragged off the edge it would be unrecoverable
    // without clearing storage. Measured, because a collapsed bar is smaller.
    const width = panel.current?.offsetWidth ?? 256;
    const height = panel.current?.offsetHeight ?? 40;
    const x = Math.max(0, Math.min(window.innerWidth - width, d.ox + (e.clientX - d.sx)));
    const y = Math.max(0, Math.min(window.innerHeight - height, d.oy + (e.clientY - d.sy)));
    posRef.current = { x, y };
    setPos(posRef.current);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (drag.current) store(POS_KEY, JSON.stringify(posRef.current));
    drag.current = null;
  };

  const toggleOldStates = () => {
    setOldStatesOpen((previous) => {
      store(OLD_STATES_KEY, String(!previous));
      return !previous;
    });
  };

  const toggleCollapsed = () => {
    setCollapsed((previous) => {
      store(COLLAPSED_KEY, String(!previous));
      return !previous;
    });
  };

  const handleReset = () => {
    resetPrototype();
    window.location.reload();
  };

  const handleFill = async () => {
    const { attached, vendor, date } = await fillUploadForm();
    if (!attached) {
      toast.error("No upload form on this screen — open Revenue → Upload new statement.");
      return;
    }
    // The two dropdowns are filled the same way a person would; say so when one
    // of them did not take, rather than leaving a half-filled form unexplained.
    const missed = [!vendor && "vendor", !date && "statement date"].filter(Boolean).join(" and ");
    if (missed) {
      toast.warning(`${MOCK_FILE_NAME} attached — pick the ${missed} by hand.`);
      return;
    }
    toast.success(`${MOCK_FILE_NAME} attached. Submit and Analyze to run it.`);
  };

  const dragHandle = (
    <button
      type="button"
      aria-label="Drag to move"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="flex cursor-grab touch-none items-center px-0.5 text-muted-foreground active:cursor-grabbing"
    >
      <GripVertical className="size-4" />
    </button>
  );

  if (collapsed) {
    return (
      <div
        ref={panel}
        className="fixed z-[100] flex w-fit items-center rounded-md border border-border bg-background p-1 shadow-md"
        style={{ left: pos.x, top: pos.y }}
      >
        {dragHandle}
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label="Expand the dev bar"
          onClick={toggleCollapsed}
        >
          <ChevronsRight className="size-4 text-muted-foreground" />
        </Button>
      </div>
    );
  }

  return (
    <div
      ref={panel}
      className="fixed z-[100] flex w-80 flex-col rounded-md border border-border bg-background shadow-md"
      style={{ left: pos.x, top: pos.y }}
    >
      <div className="flex items-center gap-1 border-b border-border p-1">
        {dragHandle}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="flex-1 justify-start pl-1"
          onClick={handleReset}
        >
          <RotateCcw className="size-4 text-muted-foreground" />
          Reset prototype
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label="Collapse the dev bar"
          onClick={toggleCollapsed}
        >
          <ChevronsLeft className="size-4 text-muted-foreground" />
        </Button>
      </div>

      <div className="border-b border-border p-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full justify-start pl-1"
          onClick={handleFill}
        >
          <FileUp className="size-4 text-muted-foreground" />
          Fill the form
        </Button>
      </div>

      <RadioGroup
        value={scenario}
        onValueChange={(next) => next && setScenario(next as ImportScenario)}
        className="flex flex-col gap-0"
      >
        <div className="flex flex-col gap-2 p-2">
          <span className="text-xs font-medium text-muted-foreground">
            This import goes wrong by
          </span>
          <div className="flex items-start gap-2">
            <RadioGroupItem
              value={FOCUS_SCENARIO.id}
              id={`scenario-${FOCUS_SCENARIO.id}`}
              className="mt-0.5"
            />
            <div className="flex flex-col gap-0.5">
              <Label
                htmlFor={`scenario-${FOCUS_SCENARIO.id}`}
                className={cn("text-xs", scenario === FOCUS_SCENARIO.id && "font-semibold")}
              >
                {FOCUS_SCENARIO.label}
              </Label>
              <span className="text-[11px] leading-snug text-muted-foreground">
                {FOCUS_SCENARIO.hint}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 pl-6">
            <span className="text-[11px] font-medium text-muted-foreground">After Save import</span>
            <SegmentedControl
              type="single"
              value={afterSave}
              onValueChange={(next) => next && setAfterSave(next as AfterSaveVariant)}
              className="w-full"
            >
              {Object.entries(AFTER_SAVE_LABELS).map(([key, label]) => (
                <SegmentedControlItem
                  key={key}
                  value={key}
                  className="flex-1 px-1.5 py-1 text-[11px]"
                >
                  {label}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          </div>

          <div className="flex flex-col gap-1.5 pl-6">
            <span className="text-[11px] font-medium text-muted-foreground">Re-import</span>
            <SegmentedControl
              type="single"
              value={reimportPlacement}
              onValueChange={(next) => next && setReimportPlacement(next as ReimportPlacement)}
              className="w-full"
            >
              {Object.entries(REIMPORT_LABELS).map(([key, label]) => (
                <SegmentedControlItem
                  key={key}
                  value={key}
                  className="flex-1 px-1.5 py-1 text-[11px]"
                >
                  {label}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          </div>

          <div className="flex flex-col gap-1.5 pl-6">
            <span className="text-[11px] font-medium text-muted-foreground">Catalog fixed</span>
            <SegmentedControl
              type="single"
              value={catalogFixed ? "yes" : "no"}
              onValueChange={(next) => next && setCatalogFixed(next === "yes")}
              className="w-full"
            >
              <SegmentedControlItem value="no" className="flex-1 px-1.5 py-1 text-[11px]">
                No
              </SegmentedControlItem>
              <SegmentedControlItem value="yes" className="flex-1 px-1.5 py-1 text-[11px]">
                Yes
              </SegmentedControlItem>
            </SegmentedControl>
          </div>
        </div>

        <div className="border-t border-border">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-start pl-1"
            onClick={toggleOldStates}
            aria-expanded={oldStatesOpen}
          >
            {oldStatesOpen ? (
              <ChevronDown className="size-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-4 text-muted-foreground" />
            )}
            Current error states
          </Button>
          {oldStatesOpen && (
            <div className="flex flex-col gap-2 p-2 pt-0">
              {SCENARIOS.map((option) => (
                <div key={option.id} className="flex items-start gap-2">
                  <RadioGroupItem
                    value={option.id}
                    id={`scenario-${option.id}`}
                    className="mt-0.5"
                  />
                  <div className="flex flex-col gap-0.5">
                    <Label
                      htmlFor={`scenario-${option.id}`}
                      className={cn("text-xs", option.id === scenario && "font-semibold")}
                    >
                      {option.label}
                    </Label>
                    <span className="text-[11px] leading-snug text-muted-foreground">
                      {option.hint}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </RadioGroup>
    </div>
  );
}
